import {
  showDepartment,
  editDepartment,
  createDepartment,
  storeDepartment,
  updateDepartment,
} from '@app/api/departments.api';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { getButtonsByStatus } from '@app/components/statusBar/actionButtons';
import StatusBar from '@app/components/statusBar/statusBar';
import { notificationController } from '@app/controllers/notificationController';
import { DepartmentRequest } from '@app/models/departments/DepartmentRequest';
import { LocationTableRow } from '@app/models/locations/location-response.model';
import { Form, notification, Tooltip, Button } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface TypeOfForm {
  type: 'edit' | 'create' | 'view';
}

interface DepartmentFormProps extends TypeOfForm {
  editable: boolean;
  id?: string;
  showEditIcon?: boolean;
  pageTitle: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}
const DepartmentForm: React.FC<DepartmentFormProps> = ({
  editable = false,
  id,
  pageTitle,
  type,
  setCurrentBreadcrumName,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const { t } = useTranslation();
  const [status, setStatus] = useState<string | null>(null);
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[]>([]);
  const [typesOptions, setTypesOptions] = useState<{ label: string; value: string }[]>([]);
  const [locationOptions, setLocationOptions] = useState<{ label: string; value: string }[]>([]);
  const [locations, setLocations] = useState([]);
  const [departmentCode, setDepartmentCode] = useState('');
  const [tableData, setTableData] = useState([]);
  const hasFetchedRef = useRef(false);

  const navigate = useNavigate();
  const departmentId = id ? parseInt(id) : null;

  const columns: ColumnsType<LocationTableRow> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: '30%',
    },
    {
      title: 'Location Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Location Address',
      dataIndex: 'address',
      key: 'address',
    },
  ];

  const setApiData = (res: any) => {
    setCurrentBreadcrumName(res?.department?.code ?? 'New');
    const mappedStatuses = res.status_options.map((status: any) => ({
      label: status.label,
      value: status.value,
    }));
    setStatusOptions(mappedStatuses ?? []);

    setLocations(res?.location_options ?? []);

    // Update the types options if needed
    const mappedTypes = res.type_options.map((type: any) => ({
      label: type.label,
      value: type.value,
    }));
    setTypesOptions(mappedTypes ?? []); // Update the typesOptions state
    setDepartmentCode(res?.department?.code ?? 'New');
    setTableData(res?.department?.locations ?? []);
    setLocationOptions(res?.location_options?.map((e: any) => ({ value: e?.id, label: e?.address })));
    setStatus(res?.department?.status);

    form.setFieldsValue({
      code: res?.department?.code,
      name: res?.department?.name,
      description: res?.department?.description,
      status:
        type == 'view'
          ? res?.status_options?.find((e: any) => e?.value == res?.department?.status)?.label
          : res?.department?.status,
      type:
        type == 'view'
          ? res?.type_options?.find((e: any) => e?.value == res?.department?.type)?.label
          : res?.department?.type,
      address: res?.department?.address,
      location_ids: res?.department?.locations?.map((e: any) => e?.id) ?? [],
    });
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    setLoading(true);
    if (departmentId) {
      if (type === 'view') {
        showDepartment(departmentId)
          .then((res: any) => {
            setApiData(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to department', description: err.message });
            console.error(err);
          });
      } else if (type === 'edit') {
        editDepartment(departmentId)
          .then((res: any) => {
            setApiData(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to edit department', description: err.message });
            console.error(err);
          });
      }
    } else if (type === 'create') {
      createDepartment()
        .then((res: any) => {
          setApiData(res);
          setTypesOptions(res.type_options);
          setLoading(false);
        })
        .catch((err: any) => {
          setLoading(false);
          notification.error({ message: 'Failed to fetch categories', description: err.message });
          console.error(err);
        });
    }
  }, []);

  useEffect(() => {
    if (editable) {
      addHeaderButton(
        'save-button',
        <BaseButton type="primary" htmlType="submit" loading={isLoading} onClick={() => form.submit()} size="small">
          {t('Save')}
        </BaseButton>,
      );

      addHeaderButton(
        'cancel-button',
        <BaseButton onClick={() => navigate('/departments')} style={{ marginRight: '8px' }} size="small">
          {t('Cancel')}
        </BaseButton>,
      );
    }

    if (!editable && id) {
      addHeaderButton(
        'edit-button',
        <BaseButton type="primary" onClick={() => navigate(`/edit-department`, { state: { id } })} size="small">
          {t('Edit')}
        </BaseButton>,
      );
    }

    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('edit-button');
    };
  }, [form, isLoading, editable, id, addHeaderButton, t, navigate, removeHeaderButton]);

  const handleSubmit = (values: DepartmentRequest) => {
    if (editable) {
      const submissionData = {
        ...values,
        ...values,
      };

      if (type === 'create') {
        storeDepartment(submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate(`/view-department`, { state: { id: res?.data?.id } });
          })
          .catch((err: any) => {
            notificationController.error({ message: err.message });
            setLoading(false);
          });
      } else if (type === 'edit' && departmentId) {
        updateDepartment(departmentId, submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate(`/view-department`, { state: { id: id } });
          })
          .catch((err: any) => {
            notificationController.error({ message: `${err.message}` });
            setLoading(false);
          });
      }
    }
    console.log('Form Submitted');
  };

  return (
    <>
      <BaseRow gutter={[16, 16]}>
        <BaseCol span={24}>
          <StatusBar
            id={departmentCode}
            status={status}
            setStatus={function (value: React.SetStateAction<string | null>): void {
              throw new Error('Function not implemented.');
            }}
            wfActions={[]}
            currentType={'edit'}
            workflowType={'product_registration'}
            onViewInvoicesClick={undefined}
          />
        </BaseCol>
        <BaseCol span={24}>
          <BaseCard
            id="departmentForm"
            title={pageTitle}
            padding="1.25rem"
            // extra={
            //   type === 'view' && (
            //     <Tooltip title="Edit">
            //       <Button type="ghost" onClick={() => navigate(`/edit-department`, { state: { id: id } })}>
            //         Edit
            //       </Button>
            //     </Tooltip>
            //   )
            // }
          >
            <Form
              form={form}
              name="departmentForm"
              onFinish={handleSubmit}
              labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
              labelAlign="left"
              labelWrap
              wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
              size="small"
            >
              <BaseRow gutter={[16, 16]}>
                <BaseCol xs={24} md={24} lg={12}>
                  <Form.Item
                    name="code"
                    label="Item Code"
                    rules={[{ required: editable, message: 'Department Code is required' }]}
                  >
                    <BaseInput placeholder="Enter Department Code" readOnly={!editable} />
                  </Form.Item>
                </BaseCol>

                <BaseCol xs={24} md={24} lg={12}>
                  <Form.Item name="name" label="Name" rules={[{ required: editable, message: 'Name is required' }]}>
                    <BaseInput placeholder="Enter Department Name" readOnly={!editable} />
                  </Form.Item>
                </BaseCol>
                <BaseCol xs={24} md={24} lg={12}>
                  <Form.Item
                    label="Status"
                    name="status"
                    rules={[{ required: editable, message: 'Status is required' }]}
                  >
                    {editable ? (
                      <BaseSelect placeholder="Select Status" options={statusOptions} />
                    ) : (
                      <BaseInput readOnly={!editable} />
                    )}
                  </Form.Item>
                </BaseCol>
                <BaseCol xs={24} md={24} lg={12}>
                  <Form.Item label="Type" name="type" rules={[{ required: editable, message: 'Type is required' }]}>
                    {editable ? (
                      <BaseSelect placeholder="Select Type" options={typesOptions} />
                    ) : (
                      <BaseInput readOnly={!editable} />
                    )}
                  </Form.Item>
                </BaseCol>
                <BaseCol xs={24} md={24} lg={12}>
                  <Form.Item
                    label="Address"
                    name="address"
                    rules={[{ required: editable, message: 'Address is required' }]}
                  >
                    {editable ? <BaseInput.TextArea placeholder="Enter Address" /> : <BaseInput readOnly={!editable} />}
                  </Form.Item>
                </BaseCol>
                <BaseCol xs={24} md={24} lg={12}>
                  <Form.Item label="Description" name="description">
                    {editable ? (
                      <BaseInput.TextArea placeholder="Enter Description" />
                    ) : (
                      <BaseInput readOnly={!editable} />
                    )}
                  </Form.Item>
                </BaseCol>
                {editable && (
                  <BaseCol xs={24} md={24} lg={12}>
                    <Form.Item
                      label="Locations"
                      name="location_ids"
                      rules={[{ required: editable, message: 'Locations are required' }]}
                    >
                      <BaseSelect
                        mode="multiple"
                        allowClear
                        placeholder="Select Department Locations"
                        options={locationOptions}
                        onChange={(e: any) => {
                          const data = locations?.filter((a: any) => e?.includes(a?.id));
                          setTableData(data);
                        }}
                      />
                    </Form.Item>
                  </BaseCol>
                )}
                <BaseCol xs={24} md={24} lg={24}>
                  <BaseTable
                    title={() => 'Locations'}
                    columns={columns}
                    dataSource={tableData}
                    rowKey="id"
                    size="small"
                    bordered
                    pagination={false}
                  />
                </BaseCol>
              </BaseRow>

              <br />
            </Form>
          </BaseCard>
        </BaseCol>
      </BaseRow>
    </>
  );
};

export default DepartmentForm;
